import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { selectViewerId } from '../../common/app/reducer'
import {
  CART_SAVE_STATUS,
  dispatchAsync,
  fetchBulkOrderCart,
  saveBulkOrderCart,
  selectCart,
  selectCartOwnerId,
  selectCartSaveStatus,
  selectIsCartLoading,
} from '../../common/bulkOrder'
import {
  CART_SAVE_DEBOUNCE_MS,
  isCartDirty,
  shouldDispatchSave,
  shouldFollowUpAfterSave,
} from './cartSyncPolicy'

const useBulkOrderCartSync = () => {
  const dispatch = useDispatch()
  const store = useStore()
  const viewerId = useSelector(selectViewerId)
  const cart = useSelector(selectCart)
  const cartOwnerId = useSelector(selectCartOwnerId)
  const isCartLoading = useSelector(selectIsCartLoading)
  const saveStatus = useSelector(selectCartSaveStatus)
  const debounceTimerRef = useRef(null)
  const inFlightSaveRef = useRef(null)
  const prevSaveStatusRef = useRef(saveStatus)
  const prevViewerIdRef = useRef(viewerId)

  const cancelPendingDebounce = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
  }, [])

  const dispatchCartSave = useCallback(
    ({ force = false } = {}) => {
      const state = store.getState()
      const cart = state.bulkOrder.cart
      if (
        !shouldDispatchSave({
          cart,
          viewerId: state.app.viewer?._id,
          force,
        })
      ) {
        // Clean cart: flush is a no-op success (Send may gate on this).
        // Dirty but not ready / wrong owner / already saving: not safe to proceed.
        return Promise.resolve(!isCartDirty(cart))
      }

      cancelPendingDebounce()
      const savePromise = dispatchAsync(dispatch, saveBulkOrderCart())
        .then(() => true)
        .catch(() => false)
        .finally(() => {
          if (inFlightSaveRef.current === savePromise) {
            inFlightSaveRef.current = null
          }
        })
      inFlightSaveRef.current = savePromise
      return savePromise
    },
    [cancelPendingDebounce, dispatch, store],
  )

  const scheduleDebouncedSave = useCallback(() => {
    cancelPendingDebounce()
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null
      dispatchCartSave()
    }, CART_SAVE_DEBOUNCE_MS)
  }, [cancelPendingDebounce, dispatchCartSave])

  const flushCartSave = useCallback(async () => {
    cancelPendingDebounce()
    if (inFlightSaveRef.current) {
      await inFlightSaveRef.current
    }
    const ok = await dispatchCartSave({ force: true })
    if (!ok) return false
    // Edit during save keeps dirty; flush must not claim success while unsaved.
    return !isCartDirty(store.getState().bulkOrder.cart)
  }, [cancelPendingDebounce, dispatchCartSave, store])

  // Fetch when the signed-in viewer does not own the in-memory cart.
  useEffect(() => {
    if (!viewerId) return
    if (viewerId === cartOwnerId) return
    dispatch(fetchBulkOrderCart())
  }, [dispatch, viewerId, cartOwnerId])

  // Cancel debounce on viewer change or while loading.
  useEffect(() => {
    const viewerChanged = prevViewerIdRef.current !== viewerId
    if (viewerChanged) {
      prevViewerIdRef.current = viewerId
      prevSaveStatusRef.current = CART_SAVE_STATUS.IDLE
    }

    if (viewerChanged || isCartLoading) {
      cancelPendingDebounce()
    }
  }, [viewerId, isCartLoading, cancelPendingDebounce])

  // Debounce saves while the cart is dirty.
  // Deps intentionally omit full `cart` (only dirty/saveStatus/load gates); save reads store.
  useEffect(() => {
    if (isCartLoading) return undefined
    if (
      !shouldDispatchSave({
        cart,
        viewerId,
      })
    ) {
      return undefined
    }

    scheduleDebouncedSave()
    return cancelPendingDebounce
  // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
  }, [
    cart.dirty,
    saveStatus,
    isCartLoading,
    viewerId,
    scheduleDebouncedSave,
    cancelPendingDebounce,
  ])

  // After an in-flight save ends, follow up if the cart is still dirty (e.g. edit mid-save).
  useEffect(() => {
    const prevSaveStatus = prevSaveStatusRef.current
    prevSaveStatusRef.current = saveStatus

    const justFinishedSaving =
      prevSaveStatus === CART_SAVE_STATUS.SAVING &&
      saveStatus !== CART_SAVE_STATUS.SAVING

    if (!justFinishedSaving) return
    if (!shouldFollowUpAfterSave({ cart, saveStatus, isCartLoading })) return

    dispatchCartSave()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only dirty gate; omit full cart
  }, [saveStatus, isCartLoading, cart.dirty, dispatchCartSave])

  // Flush pending debounce on tab hide / leave (best-effort; may still be cancelled by browser).
  useEffect(() => {
    const handlePageHide = () => {
      flushCartSave()
    }
    window.addEventListener('pagehide', handlePageHide)
    return () => {
      window.removeEventListener('pagehide', handlePageHide)
    }
  }, [flushCartSave])

  // Flush on shell unmount (e.g. catalog ↔ cart remount).
  useEffect(
    () => () => {
      flushCartSave()
    },
    [flushCartSave],
  )

  return { flushCartSave }
}

export default useBulkOrderCartSync
